import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cls from 'classnames';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

import { ReactComponent as ChevronDown } from 'assets/svg/chevron-down.svg';
import { ReactComponent as StarFilledIcon } from 'assets/images/svg/star-filled.svg';

import SpecialsBadge from 'components/shared/ui/SpecialsBadge';

import { getPriceInfo } from 'utils/priceInfoHelper';
import theme from 'utils/theme';

const CourseCard = ({
  course,
  styles,
  onClick,
  show,
  isSkeleton,
}) => {
  const price = useMemo(() => {
    if (!course || isSkeleton) return {};
    return ({
      ...getPriceInfo(course, ['minRate', '', 'additionalFeesForMinRate']),
      ...getPriceInfo(course, ['maxRate', '', 'additionalFeesForMaxRate']),
      ...getPriceInfo(course, ['maxAdditionalFees']),
    });
  }, [course, isSkeleton]);

  const renderImage = course?.id && (
    <div className="relative">
      <img src={course.photoMediumUrl} className={cls('w-full h-40 object-cover object-center', styles?.image)} alt="course-thumb" />
      {(show?.specials || show?.club) && (
        <div className="absolute left-2 bottom-2 flex gap-2">
          {course.stats.hasHotDeals && (
            <SpecialsBadge />
          )}
          {course?.hasNetworkMembership && (
            <div className="px-2 font-primary font-medium flex items-center justify-center h-6 text-3.5 leading-4 -tracking-0.4 bg-white border border-light-grey-four rounded">
              <span className="font-bold text-membership-blue">Benefits&nbsp;</span>
              <span className="text-mild-grey">with Club</span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderCourseDetails = course?.id && (
    <div className={cls('p-4 pt-3 flex flex-col flex-1 min-h-50', styles?.content)}>
      {course?.name && <h4 className="font-primary font-bold text-4 leading-5 text-dark w-full truncate">{course.name}</h4>}
      <div className="flex items-center mt-2 gap-1.5">
        {show?.distance && <p className="font-primary-two text-3.5 leading-4 -tracking-0.2 text-grey-3">{`${Math.round(course.distance)} miles away`}</p>}
        {show?.distance && course?.addressCity && <div className="w-px h-4 bg-grey-3" />}
        {course?.addressCity && <p className="font-primary-two text-3.5 leading-4 -tracking-0.2 text-grey-3">{`${course?.addressCity}, ${course?.addressState}`}</p>}
      </div>
      {show?.rating && course?.rating && (
        <div className="mt-2 flex items-center gap-0.5">
          <StarFilledIcon className="w-3 h-3 text-dark fill-current" />
          <p className="font-primary font-bold text-3.5 leading-4 -tracking-0.2 text-dark">
            {course?.rating?.valueRounded}
          </p>
          <p className="font-primary-two text-3.5 leading-4 -tracking-0.2 text-grey-3 whitespace-nowrap">
            {`(${course?.rating?.count} reviews)`}
          </p>
        </div>
      )}
      {!!price.minRate && (
        <p className="mt-3 font-primary font-black text-5 leading-5 -tracking-0.4 text-dark">
          {`${price.minRateLabel}${price.minRate !== price.maxRate ? ` - ${price.maxRateLabel}` : ''}`}
        </p>
      )}
      {!!price.maxAdditionalFees && (
        <div className="mt-1.5 flex items-center gap-1">
          <p className="font-primary text-2.5 leading-3 text-dark">
            Includes fee up to
            {' '}
            <span className="font-semibold">
              {price.maxAdditionalFeesLabel}
            </span>
          </p>
        </div>
      )}
      <div className="mt-auto space-y-4">
        {show?.discount && course?.networkMembershipDiscountPercentage && (
          <div className="w-fit mt-3 font-primary text-3.5 leading-4 -tracking-0.4 px-2 flex items-center h-8 rounded-1 border border-light-grey-four">
            <span className="font-bold text-membership-blue">{`Save an extra ${course.networkMembershipDiscountPercentage}%`}</span>
            <span className="font-medium text-mild-grey">&nbsp;with Club</span>
            <ChevronDown transform="rotate(90)" style={{ transform: 'rotate(90deg)' }} className="w-4 h-4 fill-current text-dark" />
          </div>
        )}
        {course?.hierarchizedUrl && (
          <button
            type="button"
            className="font-primary font-bold w-full h-8 border border-dark rounded-1 flex justify-center items-center text-4 leading-4 -tracking-0.4 text-dark focus:outline-none"
          >
            View course
          </button>
        )}
      </div>
    </div>
  );

  if (isSkeleton) {
    return (
      <SkeletonTheme
        color={theme.colors['white-one']}
        highlightColor={theme.colors.white}
      >
        <div className="w-68 rounded-2 overflow-hidden">
          <Skeleton width={272} height={200} />
          <div className="h-44 flex flex-col p-4 pt-3">
            <div className="leading-5 flex">
              <Skeleton width={215} height={20} />
            </div>
            <div className="mt-1 flex leading-4">
              <Skeleton width={89} height={16} />
            </div>
            <div className="mt-3 h-5 flex leading-5">
              <Skeleton width={120} height={20} />
            </div>
            <div className="mt-auto flex leading-8">
              <Skeleton height={32} className="w-full" />
            </div>
          </div>
        </div>
      </SkeletonTheme>
    );
  }

  return (
    <div
      key={course.id}
      className={cls('min-w-68 flex-1 flex flex-col border border-light-grey-four rounded-2 overflow-hidden cursor-pointer', styles?.wrapper)}
      onClick={(e) => {
        e.stopPropagation();
        onClick(course);
      }}
      aria-hidden
    >
      {renderImage}
      {renderCourseDetails}
    </div>
  );
};

CourseCard.defaultProps = {
  course: {},
  styles: {},
  onClick: () => {},
  show: {
    club: true,
    specials: true,
    discount: true,
    rating: false,
    distance: true,
  },
  isSkeleton: false,
};

CourseCard.propTypes = {
  course: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    distance: PropTypes.number,
    photoMediumUrl: PropTypes.string,
    hierarchizedUrl: PropTypes.string,
    addressCity: PropTypes.string,
    addressState: PropTypes.string,
    minRate: PropTypes.number,
    maxRate: PropTypes.number,
    additionalFees: PropTypes.number,
    symbol: PropTypes.string,
    currency: PropTypes.string,
    showSymbol: PropTypes.bool,
    rating: PropTypes.shape({
      valueRounded: PropTypes.number,
      count: PropTypes.number,
    }),
    stats: PropTypes.shape({
      hasHotDeals: PropTypes.bool,
    }),
    hasNetworkMembership: PropTypes.bool,
    networkMembershipDiscountPercentage: PropTypes.number,
  }),
  styles: PropTypes.shape({
    wrapper: PropTypes.string,
    content: PropTypes.string,
    image: PropTypes.string,
  }),
  onClick: PropTypes.func,
  show: PropTypes.shape({
    club: PropTypes.bool,
    specials: PropTypes.bool,
    discount: PropTypes.bool,
    distance: PropTypes.bool,
    rating: PropTypes.bool,
  }),
  isSkeleton: PropTypes.bool,
};

export default CourseCard;
